import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import { Download as DownloadIcon, Archive } from '@mui/icons-material';
import { ExportJob } from '@aphp-ema/common/export';
import { ExportStatusIcon } from './ExportStatusIcon';
import {
  exportHistoryListStyles,
  exportHistoryItemStyles,
  exportJobIconStyles,
  exportJobActionsStyles,
} from '../styles/exportStyles';
import { useTranslation } from 'react-i18next';
import { Researcher } from '@aphp-ema/common/researchers';
import { useOne } from '@refinedev/core';
import { useRoles } from '@/core/auth/useRoles';

type ExportHistoryListProps = {
  exportJobs: ExportJob[];
  downloadExport: (id: string) => void;
  isAdmin?: boolean;
};

type ExportHistoryListItemProps = {
  job: ExportJob;
  downloadExport: (id: string) => void;
};

const ExportHistoryListItem: React.FC<ExportHistoryListItemProps> = ({
  job,
  downloadExport,
}) => {
  const role = useRoles();
  const { data: exporterData } = useOne<Researcher>({
    resource: 'researchers',
    id: job.exporterId,
    queryOptions: {
      enabled:
        role.isSuccess && role.data?.some((role) => role.name === 'admin'),
    },
  });

  const exporterName = useMemo(() => {
    if (!exporterData?.data) return '';

    return exporterData.data.firstName + ' ' + exporterData.data.lastName;
  }, [exporterData]);

  return (
    <ListItem sx={exportHistoryItemStyles}>
      <Box sx={exportJobIconStyles}>
        <Archive fontSize="small" />
      </Box>
      <ListItemText
        sx={{ p: 1, paddingRight: 32 }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">
              {new Date(job.createdAt).toLocaleString()}
            </Typography>
          </Box>
        }
        secondary={exporterName}
      />
      <Box sx={exportJobActionsStyles}>
        <ExportStatusIcon status={job.status} error={job.error} />
        {job.status === 'COMPLETED' && job.objectKey && (
          <IconButton
            component="a"
            onClick={() => downloadExport(job.id)}
            rel="noopener noreferrer"
            download="export.json"
            size="small"
            color="primary"
          >
            <DownloadIcon />
          </IconButton>
        )}
      </Box>
    </ListItem>
  );
};

export const ExportHistoryList: React.FC<ExportHistoryListProps> = ({
  exportJobs,
  downloadExport,
}) => {
  const { t } = useTranslation();
  if (exportJobs.length === 0) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('pages.studies.export.recentExports')}
      </Typography>
      <List sx={exportHistoryListStyles}>
        {exportJobs.map((job, index) => (
          <React.Fragment key={job.id}>
            <ExportHistoryListItem job={job} downloadExport={downloadExport} />
            {index < exportJobs.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};
